/**
 * Page Team - Gestion des vendeurs et livreurs
 * Onglet Vendeurs : accès via cases à cocher, rôle dérivé automatiquement.
 * Onglet Livreurs : gestion de l'équipe de livraison (CRUD + activation/désactivation).
 */
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { api } from '../services/api';
import { logger } from '../utils/logger';

type TeamTab = 'vendors' | 'drivers';

type VendorRoleType = 'ADMIN' | 'MANAGER' | 'CASHIER' | 'CASHIER_PLUS' | 'FIELD';

interface Vendor {
  id: string;
  name: string;
  email: string | null;
  role: VendorRoleType;
  active: boolean;
  has_pin?: boolean;
  created_at: string;
  updated_at: string;
  workstation_codes?: string[];
}

interface Workstation {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

// ═══════════════════════════════════════════════════════════
// Définition unifiée des 8 accès (checkboxes)
// ═══════════════════════════════════════════════════════════

interface WorkstationDef {
  label: string;
  description: string;
  icon: string;
  requiresPin: boolean;
}

const ALL_WORKSTATIONS: Record<string, WorkstationDef> = {
  POS: {
    label: 'Caisse (POS)',
    description: 'Point de vente, encaissement',
    icon: '🛒',
    requiresPin: false,
  },
  STOCKS: {
    label: 'Stocks',
    description: 'Gestion des stocks, réceptions',
    icon: '📊',
    requiresPin: false,
  },
  CLOSING: {
    label: 'Clôture',
    description: 'Rapport Z, historique pertes',
    icon: '📋',
    requiresPin: false,
  },
  PREPARATION: {
    label: 'Préparation',
    description: 'Préparation des commandes',
    icon: '📦',
    requiresPin: true,
  },
  LIVE: {
    label: 'Live Shopping',
    description: 'Vente en direct',
    icon: '📺',
    requiresPin: true,
  },
  CHAT: {
    label: 'Messagerie',
    description: 'Conversations avec les clients',
    icon: '💬',
    requiresPin: true,
  },
  DELIVERY: {
    label: 'Livraison',
    description: 'Application livreur : tournées à domicile, preuves de livraison',
    icon: '🚚',
    requiresPin: true,
  },
  FIELD: {
    label: 'Terrain',
    description: 'Application terrain (tâches, récoltes)',
    icon: '🌱',
    requiresPin: true,
  },
  DASHBOARD: {
    label: 'Dashboard',
    description: 'Tableau de bord, configuration, analytics',
    icon: '👑',
    requiresPin: true,
  },
};

// Groupes pour l'affichage
const BASIC_CODES = ['POS', 'STOCKS', 'CLOSING'];
const EXTENDED_CODES = ['PREPARATION', 'LIVE', 'CHAT'];
// « Livraison » est un accès à une AUTRE application, comme « Terrain » — pas
// un poste tenu dans le magasin. D'où sa place ici, et non parmi les postes
// étendus : un vendeur qui ne fait que livrer n'a rien à faire sur la caisse.
const SPECIAL_CODES = ['FIELD', 'DELIVERY', 'DASHBOARD'];
// Les SIX postes qui font un manager. « Livraison » n'en fait volontairement
// PAS partie : l'y ajouter retirerait leur rôle à tous les managers en place,
// qui ne l'ont pas coché. Un poste nouveau ne réécrit pas les rôles existants.
const POS_WORKSTATION_CODES = ['POS', 'PREPARATION', 'LIVE', 'STOCKS', 'CHAT', 'CLOSING'];

// ═══════════════════════════════════════════════════════════
// Fonctions de dérivation
// ═══════════════════════════════════════════════════════════

function deriveRole(workstations: string[]): VendorRoleType {
  if (workstations.includes('DASHBOARD')) return 'ADMIN';
  if (POS_WORKSTATION_CODES.every(c => workstations.includes(c))) return 'MANAGER';
  if (EXTENDED_CODES.some(c => workstations.includes(c))) return 'CASHIER_PLUS';
  if (BASIC_CODES.some(c => workstations.includes(c))) return 'CASHIER';
  // Rôle interne « FIELD » = personnel HORS caisse (VendorQuickBar l'exclut).
  // Il couvre le terrain comme la livraison : ni l'un ni l'autre ne tient un
  // poste dans le magasin.
  if (workstations.includes('FIELD') || workstations.includes('DELIVERY')) return 'FIELD';
  return 'CASHIER';
}

/**
 * Libellé du badge. Le rôle interne « FIELD » couvre deux métiers distincts :
 * afficher « Terrain » à un livreur serait faux.
 */
function roleLabel(role: string, workstations: string[]): string {
  const base = ROLE_DISPLAY[role]?.label || role;
  if (role !== 'FIELD') return base;
  const terrain = workstations.includes('FIELD');
  const livraison = workstations.includes('DELIVERY');
  if (terrain && livraison) return 'Terrain et livraison';
  if (livraison) return 'Livraison';
  return base;
}

function needsPin(workstations: string[]): boolean {
  return workstations.some(code => ALL_WORKSTATIONS[code]?.requiresPin === true);
}

// Affichage du rôle dérivé
const ROLE_DISPLAY: Record<string, { label: string; icon: string; color: string }> = {
  ADMIN: { label: 'Administrateur', icon: '👑', color: 'bg-yellow-100 text-yellow-800' },
  MANAGER: { label: 'Manager', icon: '⭐', color: 'bg-purple-100 text-purple-800' },
  CASHIER_PLUS: { label: 'Vendeur+', icon: '🎯', color: 'bg-green-100 text-green-800' },
  CASHIER: { label: 'Vendeur', icon: '👤', color: 'bg-blue-100 text-blue-800' },
  FIELD: { label: 'Terrain', icon: '🌱', color: 'bg-emerald-100 text-emerald-800' },
};

// ═══════════════════════════════════════════════════════════
// Composant checkbox réutilisable
// ═══════════════════════════════════════════════════════════

function WorkstationCheckbox({
  code,
  checked,
  onChange,
}: {
  code: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const info = ALL_WORKSTATIONS[code];
  if (!info) return null;
  return (
    <label className="flex items-center gap-4 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-6 h-6 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
      <span className="text-2xl">{info.icon}</span>
      <div className="flex-1">
        <span className="text-base font-medium text-gray-800">{info.label}</span>
        <p className="text-sm text-gray-500">{info.description}</p>
      </div>
      {info.requiresPin && (
        <span className="text-xs text-gray-400">PIN</span>
      )}
    </label>
  );
}

// ═══════════════════════════════════════════════════════════
// Composant principal
// ═══════════════════════════════════════════════════════════

type ViewMode = 'list' | 'add' | 'edit';

export default function Team() {
  const { tenant } = useTenant();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TeamTab>(
    searchParams.get('tab') === 'drivers' ? 'drivers' : 'vendors'
  );
  const [vendors, setVendors] = useState<Vendor[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_workstations, setWorkstations] = useState<Workstation[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [editingWorkstations, setEditingWorkstations] = useState<string[]>([]);
  const [editPin, setEditPin] = useState('');
  const [newVendor, setNewVendor] = useState({
    name: '',
    email: '',
    pin: '',
    workstations: ['POS'] as string[],
  });

  // Le menu « Livraisons › Livreurs » mène ici avec ?tab=drivers, y compris
  // quand la page est déjà ouverte : l'onglet suit l'adresse.
  const ongletDemande = searchParams.get('tab');
  useEffect(() => {
    if (ongletDemande === 'drivers' || ongletDemande === 'vendors') setActiveTab(ongletDemande);
  }, [ongletDemande]);

  useEffect(() => {
    if (tenant) {
      loadVendors();
      loadWorkstations();
    }
  }, [tenant]);

  const loadVendors = async () => {
    if (!tenant) return;
    try {
      const data = await api.listVendors(tenant.id);

      const vendorsWithWorkstations = await Promise.all(
        data.map(async (vendor: Vendor) => {
          try {
            const codes = await api.getVendorWorkstationCodes(vendor.id);
            return { ...vendor, workstation_codes: codes };
          } catch {
            return { ...vendor, workstation_codes: [] };
          }
        })
      );

      setVendors(vendorsWithWorkstations);
    } catch (error) {
      logger.error('Failed to load vendors:', error);
      alert('Erreur lors du chargement des vendeurs');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkstations = async () => {
    try {
      let data = await api.getWorkstations(true);

      // Créer les postes manquants. Le seuil suit le nombre de postes définis
      // ci-dessus : sans cette ligne, le poste ajouté aujourd'hui (« Livraison »)
      // n'existerait dans aucun tenant déjà servi, et sa case resterait sans effet.
      if (data.length < Object.keys(ALL_WORKSTATIONS).length) {
        try {
          const seedResult = await api.seedWorkstations();
          if (seedResult.created?.length > 0) {
            logger.log(`Workstations créées: ${seedResult.created.join(', ')}`);
            data = await api.getWorkstations(true);
          }
        } catch (seedError) {
          logger.error('Failed to seed workstations:', seedError);
        }
      }

      setWorkstations(data);
    } catch (error) {
      logger.error('Failed to load workstations:', error);
    }
  };

  /**
   * Déplace un vendeur d'un cran sur la barre du POS.
   * On réécrit TOUTE la série (0,1,2…) plutôt que d'échanger deux valeurs : les
   * positions héritées valent toutes 100 par défaut, un simple échange ne
   * produirait aucun mouvement visible.
   */
  const moveVendor = async (vendor: Vendor, delta: number) => {
    const ordered = [...vendors];
    const from = ordered.indexOf(vendor);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ordered.length) return;
    ordered.splice(to, 0, ordered.splice(from, 1)[0]);
    setVendors(ordered); // retour immédiat, la caisse suivra au rechargement
    try {
      await Promise.all(
        ordered.map((v, i) => api.updateVendor(v.id, { display_order: i })),
      );
    } catch (error) {
      logger.error('Erreur réordonnancement vendeurs:', error);
      loadVendors();
    }
  };

  const handleToggleActive = async (vendor: Vendor) => {
    if (!tenant) return;

    try {
      await api.updateVendor(vendor.id, { active: !vendor.active });

      setVendors((prev) =>
        prev.map((v) =>
          v.id === vendor.id ? { ...v, active: !v.active } : v
        )
      );

      logger.log(`Vendeur ${vendor.name} ${!vendor.active ? 'activé' : 'désactivé'}`);
    } catch (error) {
      logger.error('Failed to update vendor:', error);
      alert('Erreur lors de la mise à jour du vendeur');
    }
  };

  // Rôle dérivé pour le formulaire d'ajout
  const newVendorDerivedRole = useMemo(() => deriveRole(newVendor.workstations), [newVendor.workstations]);
  const newVendorNeedsPin = useMemo(() => needsPin(newVendor.workstations), [newVendor.workstations]);

  // Rôle dérivé pour le formulaire d'édition
  const editDerivedRole = useMemo(() => deriveRole(editingWorkstations), [editingWorkstations]);
  const editNeedsPin = useMemo(() => needsPin(editingWorkstations), [editingWorkstations]);

  const handleAddVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant) return;

    if (newVendorNeedsPin && !newVendor.pin) {
      alert('Le code PIN est obligatoire pour les accès sélectionnés');
      return;
    }

    try {
      const createdVendor = await api.createVendor(tenant.id, {
        name: newVendor.name,
        email: newVendor.email || null,
        pin: newVendor.pin || undefined,
        role: newVendorDerivedRole,
      });

      if (newVendor.workstations.length > 0) {
        try {
          await api.setVendorWorkstations(createdVendor.id, newVendor.workstations);
          logger.log(`Workstations assignées: ${newVendor.workstations.join(', ')}`);
        } catch (err) {
          logger.warn('Failed to assign workstations:', err);
        }
      }

      await loadVendors();
      setViewMode('list');
      setNewVendor({ name: '', email: '', pin: '', workstations: ['POS'] });
      logger.log('Vendeur créé avec succès');
    } catch (error) {
      logger.error('Failed to create vendor:', error);
      alert('Erreur lors de la création du vendeur');
    }
  };

  const handleEditVendor = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setEditingWorkstations(vendor.workstation_codes || []);
    setEditPin('');
    setViewMode('edit');
  };

  const handleUpdateVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant || !editingVendor) return;

    try {
      const updateData: Record<string, unknown> = {
        name: editingVendor.name,
        email: editingVendor.email || null,
        role: editDerivedRole,
      };

      if (editPin && editPin.length >= 4) {
        updateData.pin = editPin;
      }

      await api.updateVendor(editingVendor.id, updateData);

      try {
        await api.setVendorWorkstations(editingVendor.id, editingWorkstations);
        logger.log(`Workstations mises à jour: ${editingWorkstations.join(', ')}`);
      } catch (err) {
        logger.warn('Failed to update workstations:', err);
      }

      await loadVendors();
      setViewMode('list');
      setEditingVendor(null);
      setEditingWorkstations([]);
      setEditPin('');
      logger.log('Vendeur mis à jour avec succès');
    } catch (error) {
      logger.error('Failed to update vendor:', error);
      alert('Erreur lors de la mise à jour du vendeur');
    }
  };

  // Toggle workstation dans newVendor
  const toggleNewWorkstation = (code: string, checked: boolean) => {
    setNewVendor(prev => ({
      ...prev,
      workstations: checked
        ? [...prev.workstations, code]
        : prev.workstations.filter(c => c !== code),
    }));
  };

  // Toggle workstation dans editingWorkstations
  const toggleEditWorkstation = (code: string, checked: boolean) => {
    setEditingWorkstations(prev =>
      checked ? [...prev, code] : prev.filter(c => c !== code)
    );
  };

  // ═══════════════════════════════════════════════════════
  // Section checkboxes réutilisable
  // ═══════════════════════════════════════════════════════

  const renderWorkstationCheckboxes = (
    selectedCodes: string[],
    onToggle: (code: string, checked: boolean) => void,
    derivedRole: VendorRoleType,
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Accès et permissions
      </label>

      {/* Postes POS de base */}
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Postes de base
      </p>
      <div className="space-y-2 mb-4">
        {BASIC_CODES.map(code => (
          <WorkstationCheckbox
            key={code}
            code={code}
            checked={selectedCodes.includes(code)}
            onChange={(checked) => onToggle(code, checked)}
          />
        ))}
      </div>

      {/* Postes étendus */}
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Postes étendus <span className="text-gray-400 font-normal">(PIN requis)</span>
      </p>
      <div className="space-y-2 mb-4">
        {EXTENDED_CODES.map(code => (
          <WorkstationCheckbox
            key={code}
            code={code}
            checked={selectedCodes.includes(code)}
            onChange={(checked) => onToggle(code, checked)}
          />
        ))}
      </div>

      {/* Accès spéciaux */}
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Accès spéciaux <span className="text-gray-400 font-normal">(PIN requis)</span>
      </p>
      <div className="space-y-2 mb-4">
        {SPECIAL_CODES.map(code => (
          <WorkstationCheckbox
            key={code}
            code={code}
            checked={selectedCodes.includes(code)}
            onChange={(checked) => onToggle(code, checked)}
          />
        ))}
      </div>

      {/* Badge rôle dérivé */}
      <div className="p-3 bg-gray-50 rounded-lg flex items-center gap-2">
        <span className="text-sm text-gray-600">Rôle :</span>
        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-full ${ROLE_DISPLAY[derivedRole]?.color || 'bg-gray-100 text-gray-800'}`}>
          {derivedRole === 'FIELD' && selectedCodes.includes('DELIVERY') && !selectedCodes.includes('FIELD')
            ? '🚚'
            : ROLE_DISPLAY[derivedRole]?.icon}{' '}
          {roleLabel(derivedRole, selectedCodes)}
        </span>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════
  // Loading
  // ═══════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════
  // Vue Ajout vendeur
  // ═══════════════════════════════════════════════════════

  if (viewMode === 'add') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => {
              setViewMode('list');
              setNewVendor({ name: '', email: '', pin: '', workstations: ['POS'] });
            }}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la liste
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Ajouter un vendeur
          </h2>

          <form onSubmit={handleAddVendor} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom complet *
              </label>
              <input
                type="text"
                value={newVendor.name}
                onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="Ex: Marie Dupont"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email (optionnel)
              </label>
              <input
                type="email"
                value={newVendor.email}
                onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="marie@example.com"
              />
            </div>

            {/* Checkboxes accès */}
            {renderWorkstationCheckboxes(newVendor.workstations, toggleNewWorkstation, newVendorDerivedRole)}

            {/* PIN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code PIN (4-6 chiffres) {newVendorNeedsPin ? '*' : '(optionnel)'}
              </label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]{4,6}"
                value={newVendor.pin}
                onChange={(e) =>
                  setNewVendor({
                    ...newVendor,
                    pin: e.target.value.replace(/\D/g, ''),
                  })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                placeholder="••••"
                maxLength={6}
                required={newVendorNeedsPin}
              />
              <p className="mt-1 text-sm text-gray-500">
                {newVendorNeedsPin
                  ? 'Le code PIN est obligatoire pour les accès sélectionnés.'
                  : 'Le code PIN est optionnel pour ces accès.'}
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => {
                  setViewMode('list');
                  setNewVendor({ name: '', email: '', pin: '', workstations: ['POS'] });
                }}
                className="flex-1 px-6 py-4 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-lg"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-lg"
              >
                Créer le vendeur
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════
  // Vue Modification vendeur
  // ═══════════════════════════════════════════════════════

  if (viewMode === 'edit' && editingVendor) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => {
              setViewMode('list');
              setEditingVendor(null);
              setEditingWorkstations([]);
              setEditPin('');
            }}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la liste
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Modifier le vendeur
          </h2>

          <form onSubmit={handleUpdateVendor} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom complet *
              </label>
              <input
                type="text"
                value={editingVendor.name}
                onChange={(e) =>
                  setEditingVendor({ ...editingVendor, name: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="Ex: Marie Dupont"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email (optionnel)
              </label>
              <input
                type="email"
                value={editingVendor.email || ''}
                onChange={(e) =>
                  setEditingVendor({ ...editingVendor, email: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="marie@example.com"
              />
            </div>

            {/* Checkboxes accès */}
            {renderWorkstationCheckboxes(editingWorkstations, toggleEditWorkstation, editDerivedRole)}

            {/* PIN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {editingVendor.has_pin
                  ? 'Nouveau code PIN (4-6 chiffres) - Laisser vide pour ne pas modifier'
                  : `Code PIN (4-6 chiffres) ${editNeedsPin ? '*' : '(optionnel)'}`}
              </label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]{4,6}"
                value={editPin}
                onChange={(e) => setEditPin(e.target.value.replace(/\D/g, ''))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                placeholder="••••"
                maxLength={6}
              />
              <p className="mt-1 text-sm text-gray-500">
                {editingVendor.has_pin
                  ? 'Laissez vide pour conserver le code PIN actuel'
                  : editNeedsPin
                    ? 'Le code PIN est obligatoire pour les accès sélectionnés.'
                    : 'Le code PIN est optionnel pour ces accès.'}
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => {
                  setViewMode('list');
                  setEditingVendor(null);
                  setEditingWorkstations([]);
                  setEditPin('');
                }}
                className="flex-1 px-6 py-4 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-lg"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-lg"
              >
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Livreurs = vendeurs portant le poste « Livraison » (même règle que l'API).
  const livreurs = vendors.filter((v) => (v.workstation_codes || []).includes('DELIVERY'));

  // ═══════════════════════════════════════════════════════
  // Vue Liste (par défaut) — avec onglets Vendeurs / Livreurs
  // ═══════════════════════════════════════════════════════

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Équipe</h1>
            <p className="mt-2 text-gray-600">
              Gérez les vendeurs et les livreurs
            </p>
          </div>
          {activeTab === 'vendors' ? (
            <button
              onClick={() => setViewMode('add')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md"
            >
              + Ajouter un vendeur
            </button>
          ) : (
            <button
              onClick={() => setActiveTab('vendors')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md"
            >
              + Ajouter un livreur
            </button>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('vendors')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'vendors'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Vendeurs ({vendors.length})
        </button>
        <button
          onClick={() => setActiveTab('drivers')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'drivers'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Livreurs ({livreurs.length})
        </button>
      </div>

      {/* === Onglet Vendeurs === */}
      {activeTab === 'vendors' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actif</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Nom</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Rôle</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Accès</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {vendors.map((vendor) => {
                const roleDisplay = ROLE_DISPLAY[vendor.role] || ROLE_DISPLAY.CASHIER;
                const codes = vendor.workstation_codes || [];
                return (
                  <tr
                    key={vendor.id}
                    className={`hover:bg-gray-50 transition-colors ${!vendor.active ? 'opacity-50' : ''}`}
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={vendor.active}
                        onChange={() => handleToggleActive(vendor)}
                        className="w-6 h-6 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {/* Position sur la barre du POS. L'ordre alphabétique ne reflète
                            pas la disposition du comptoir : avec quatre boutons tapés
                            cent fois par jour, chacun doit se retrouver là où il se tient. */}
                        <div className="flex flex-col">
                          <button
                            onClick={() => moveVendor(vendor, -1)}
                            disabled={vendors.indexOf(vendor) === 0}
                            className="px-1 text-gray-400 hover:text-gray-800 disabled:opacity-20 leading-none"
                            title="Déplacer vers la gauche sur la caisse"
                          >
                            ▲
                          </button>
                          <button
                            onClick={() => moveVendor(vendor, 1)}
                            disabled={vendors.indexOf(vendor) === vendors.length - 1}
                            className="px-1 text-gray-400 hover:text-gray-800 disabled:opacity-20 leading-none"
                            title="Déplacer vers la droite sur la caisse"
                          >
                            ▼
                          </button>
                        </div>
                        <span className="text-2xl">{roleDisplay.icon}</span>
                        <div>
                          <span className="text-base font-medium text-gray-900">{vendor.name}</span>
                          {vendor.has_pin && <span className="ml-2 text-xs text-green-600">🔐 PIN</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${roleDisplay.color}`}>
                        {roleDisplay.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {codes.length > 0 ? codes.map((code) => (
                          <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded" title={ALL_WORKSTATIONS[code]?.description}>
                            {ALL_WORKSTATIONS[code]?.icon} {ALL_WORKSTATIONS[code]?.label || code}
                          </span>
                        )) : (
                          <span className="text-xs text-orange-500">Aucun accès</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button onClick={() => handleEditVendor(vendor)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                        Modifier
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* === Onglet Livreurs === */}
      {/* Un livreur est un membre de l'équipe qui porte le poste « Livraison »
          (comme « Terrain » pour l'app des champs). L'ancienne table des
          livreurs n'est plus lue par rien : ni l'app livreur ni les tournées. */}
      {activeTab === 'drivers' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
            Un livreur est un membre de l'équipe avec le poste <strong>« Livraison »</strong> coché sur sa fiche.
            Il se connecte à l'application livreur avec son code PIN, et apparaît dans le choix du livreur d'une tournée,
            même s'il n'est pas en caisse aujourd'hui.
          </p>
          {livreurs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-4xl mb-3">🚚</p>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun livreur</h3>
              <p className="text-gray-600 mb-6">Ouvrez la fiche d'un vendeur et cochez « Livraison ».</p>
              <button onClick={() => setActiveTab('vendors')} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                Voir les vendeurs
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Nom</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Code PIN</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">En caisse aujourd'hui</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {livreurs.map((vendor) => (
                    <tr key={vendor.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">🚚</span>
                          <span className="text-base font-medium text-gray-900">{vendor.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {vendor.has_pin
                          ? <span className="text-green-700">🔐 défini</span>
                          : <span className="text-red-700 font-medium">à définir — sans PIN, pas de connexion</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{vendor.active ? 'Oui' : 'Non (peut quand même livrer)'}</td>
                      <td className="px-6 py-4">
                        <button onClick={() => handleEditVendor(vendor)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                          Modifier
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
